/**
 * 이미지 업로드 유틸리티
 *
 * ## 기본 사용법 (원스텝)
 * ```typescript
 * const result = await uploadImageWithMetadata(bucket, file, options);
 * ```
 *
 * ## 단계별 사용법 (UX 최적화)
 * ```typescript
 * // 1. 준비 단계 (썸네일 생성 포함)
 * const prepResult = await prepareImageForUpload(file, true);
 *
 * // 2. UI에서 미리보기 표시
 * if (prepResult.thumbnailResult) {
 *   // 썸네일 미리보기 사용
 *   setPreviewUrl(prepResult.thumbnailResult.previewUrl);
 * } else {
 *   // 원본 파일 미리보기 사용
 *   setPreviewUrl(URL.createObjectURL(file));
 * }
 *
 * // 3. 사용자 확인 후 업로드
 * const result = await uploadPreparedImage(bucket, file, prepResult, options);
 * ```
 *
 * ## 썸네일 옵션
 * - `generateThumbnailOption: true` - 썸네일 생성 후 미리보기 표시
 * - `generateThumbnailOption: false` - 원본 이미지로 미리보기 표시
 *
 * ## 이미지 삭제
 * 삭제 관련 기능은 `image-util-deletion.ts`를 참조하세요.
 */
import type { GoodunclesService } from '@gooduncles/gu-app-schema/dist/base';
import type { ImageFile, ImageVariant, OwnerRef } from '@gooduncles/gu-app-schema/dist/storage';
import imageCompression, { Options } from 'browser-image-compression';
import {
  CACHE_ADAY,
  COMPRESS_FILE_TYPE,
  COMMERCE_NOTICE_PATH,
  ISSUE_CASE_PATH,
  ISSUE_TASK_PATH,
  QUALITY,
} from 'src/constants/storage.constants';

import { formatDate } from './date-util';
import {
  batchEnd,
  batchStart,
  createImageFileOriginal,
  createImageFileThumbnail,
  getDownloadURL,
  getImageFileOriginalId,
  storageUploadFile,
} from './firebase/firebase-short-cut';

/**
 * 이미지 파일에서 메타데이터를 추출합니다
 */
export const extractImageMetadata = (
  file: File,
): Promise<{
  dimensions: { width: number; height: number };
  fileSize: number;
}> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        dimensions: {
          width: img.naturalWidth,
          height: img.naturalHeight,
        },
        fileSize: Math.round(file.size / 1024), // KB 단위로 변환
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
};

/**
 * 썸네일 이미지를 생성합니다 (파일만 반환)
 */
export const generateThumbnail = async (file: File, maxWidth = 240, maxHeight = 240): Promise<File> => {
  const options: Options = {
    maxWidthOrHeight: Math.max(maxWidth, maxHeight),
    useWebWorker: true,
    fileType: COMPRESS_FILE_TYPE,
    initialQuality: QUALITY,
  };

  try {
    const compressedFile = await imageCompression(file, options);
    return compressedFile;
  } catch (error) {
    console.error('Thumbnail generation failed:', error);
    throw new Error('Failed to generate thumbnail');
  }
};

/**
 * 썸네일을 생성하고 미리보기 URL과 메타데이터를 포함한 결과를 반환합니다
 */
export const generateThumbnailWithPreview = async (
  file: File,
  maxWidth = 240,
  maxHeight = 240,
): Promise<ThumbnailResult> => {
  try {
    // 썸네일 생성
    const thumbnailFile = await generateThumbnail(file, maxWidth, maxHeight);

    // 썸네일 메타데이터 추출
    const metadata = await extractImageMetadata(thumbnailFile);

    // 미리보기용 blob URL 생성
    const previewUrl = URL.createObjectURL(thumbnailFile);

    return {
      thumbnailFile,
      previewUrl,
      metadata,
    };
  } catch (error) {
    console.error('Thumbnail generation with preview failed:', error);
    throw error;
  }
};

/**
 * MIME 타입에서 파일 확장자를 얻습니다
 */
const getExtensionFromMimeType = (mimeType: string): string => {
  const mimeToExtension: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
  };
  return mimeToExtension[mimeType] || 'jpg';
};

/**
 * Storage 경로를 생성합니다 (파일의 MIME 타입 기반)
 */
export const createImageStoragePath = (
  owner: OwnerRef | null,
  variant: ImageVariant,
  fileId: string,
  file: File,
): string => {
  const fileExtension = getExtensionFromMimeType(file.type);
  const sanitizedFileName = `${fileId}.${fileExtension}`;

  if (!owner) {
    return `general/${variant}/${sanitizedFileName}`;
  }

  switch (owner.entity) {
    case 'issueCase':
      return `${ISSUE_CASE_PATH}/${owner.caseId}/${variant}/${sanitizedFileName}`;
    case 'issueTask':
      return `${ISSUE_TASK_PATH}/${owner.caseId}/${owner.taskId}/${variant}/${sanitizedFileName}`;
    case 'commerceNotice':
      return `${COMMERCE_NOTICE_PATH}/${owner.noticeId}/${variant}/${sanitizedFileName}`;
    default:
      return `general/${variant}/${sanitizedFileName}`;
  }
};

/**
 * Firebase Storage에 이미지를 업로드합니다
 */
export const uploadImageToStorage = async (bucket: string, file: File, storagePath: string): Promise<string> => {
  try {
    const uploadResult = await storageUploadFile(bucket, storagePath, file, {
      contentType: file.type,
      customMetadata: {
        // 최초 요청 시점부터 100일 동안 캐시
        cacheControl: `public,max-age=${CACHE_ADAY * 100}`,
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
      },
    });

    const downloadURL = await getDownloadURL(uploadResult.ref);
    return downloadURL;
  } catch (error) {
    console.error('Storage upload failed:', error);
    throw new Error('Failed to upload image to storage');
  }
};

/**
 * 이미지 업로드의 전체 워크플로우를 처리합니다
 */
export interface UploadImageOptions {
  /** 업로드하는 서비스 */
  service: GoodunclesService;
  /** 업로드한 사용자 ID */
  uploaderId: string;
  /** 소유자 정보 */
  owner: OwnerRef;
  /** 설명 또는 캡션 */
  description?: string;
}

export interface UploadImageResult {
  imageFileId: string;
  result: {
    /** 이미지 링크 */
    originalUrl: string;
    /** 썸네일 이미지 링크 */
    thumbnailUrl: string;
  };
}

/**
 * 썸네일 생성 결과
 */
export interface ThumbnailResult {
  /** 썸네일 파일 */
  thumbnailFile: File;
  /** 썸네일 미리보기 URL (blob URL) */
  previewUrl: string;
  /** 썸네일 메타데이터 */
  metadata: {
    dimensions: { width: number; height: number };
    fileSize: number;
  };
}

/**
 * 이미지 처리 상태
 */
export type ImageProcessingStatus = 'idle' | 'generating-thumbnail' | 'uploading' | 'completed' | 'error';

/**
 * 업로드 준비 결과
 */
export interface UploadPrepResult {
  /** 이미지 파일 ID */
  imageFileId: string;
  /** 원본 파일 메타데이터 */
  originalMetadata: {
    dimensions: { width: number; height: number };
    fileSize: number;
  };
  /** 썸네일 결과 */
  thumbnailResult: ThumbnailResult;
}

/**
 * 업로드 전 준비 작업을 수행합니다 (썸네일 생성, 메타데이터 추출, ID 생성)
 */
export const prepareImageForUpload = async (file: File): Promise<UploadPrepResult> => {
  try {
    // 1. 원본 파일 메타데이터 추출
    const originalMetadata = await extractImageMetadata(file);

    // 2. ID 생성
    const imageFileId = getImageFileOriginalId();

    // 3. 썸네일 생성
    const thumbnailResult = await generateThumbnailWithPreview(file);

    return {
      imageFileId,
      originalMetadata,
      thumbnailResult,
    };
  } catch (error) {
    console.error('Image preparation failed:', error);
    throw error;
  }
};

/**
 * 준비된 이미지를 업로드하고 메타 정보 문서를 생성합니다.
 */
export const createImageFileDocuments = async (
  bucket: string,
  file: File,
  prepResult: UploadPrepResult,
  options: UploadImageOptions,
): Promise<UploadImageResult> => {
  const { service, uploaderId, owner, description = null } = options;

  const { originalMetadata, thumbnailResult, imageFileId } = prepResult;

  try {
    // 1. Storage 경로 생성
    const originalStoragePath = createImageStoragePath(owner, 'original', imageFileId, file);
    const thumbnailStoragePath = createImageStoragePath(owner, 'thumbnail', imageFileId, thumbnailResult.thumbnailFile);

    // 2. 배치 업로드 실행
    const uploadPromises: Promise<any>[] = [];

    // 원본 업로드
    uploadPromises.push(uploadImageToStorage(bucket, file, originalStoragePath));

    // 썸네일 업로드
    uploadPromises.push(uploadImageToStorage(bucket, thumbnailResult.thumbnailFile, thumbnailStoragePath));

    const uploadResults = await Promise.all(uploadPromises);
    const originalUrl = uploadResults[0];
    const thumbnailUrl = thumbnailResult ? uploadResults[1] : null;

    // 3. 레코드 생성
    const uploadedAt = formatDate(new Date(), "yyyy-MM-dd'T'HH:mm:ss+0900");

    // NOTE: 콘솔 업로드는 imageFile 문서에 `status`를 의도적으로 넣지 않는다.
    // gu-functions 의 AVIF 변환 트리거(onImageFile*Created)는 `status === 'processing'`
    // 인 문서만 처리하므로, status 없는 이 문서들은 변환 파이프라인을 타지 않는다.
    // → 업로드한 원본 URL이 그대로 영구 사용되며(변환으로 URL이 바뀌지 않음),
    //   owner 문서의 images Map 도 트리거가 아니라 호출측(예: CommerceNoticeEditor)이
    //   클라이언트에서 직접 채운다. (status='processing' 은 native 앱 업로드 전용)
    // 원본 레코드
    const imageFileOriginal: ImageFile = {
      url: originalUrl,
      fileName: file.name,
      uploadedAt,
      fileSize: originalMetadata.fileSize,
      description,
      contentType: file.type,
      dimensions: originalMetadata.dimensions,
      service,
      uploaderId,
      bucket,
      storagePath: originalStoragePath,
      gsUri: `gs://${bucket}/${originalStoragePath}`,
      owner,
    };

    batchStart();
    await createImageFileOriginal(imageFileId, imageFileOriginal, { bBatch: true });

    // 썸네일 레코드
    const imageFileThumbnail: ImageFile = {
      url: thumbnailUrl,
      fileName: `thumbnail_${file.name}`,
      uploadedAt,
      fileSize: thumbnailResult.metadata.fileSize,
      description,
      contentType: thumbnailResult.thumbnailFile.type,
      dimensions: thumbnailResult.metadata.dimensions,
      service,
      uploaderId,
      bucket,
      storagePath: thumbnailStoragePath,
      gsUri: `gs://${bucket}/${thumbnailStoragePath}`,
      owner,
    };
    await createImageFileThumbnail(imageFileId, imageFileThumbnail, { bBatch: true });
    await batchEnd();
    // 4. 썸네일 미리보기 URL 정리
    if (thumbnailResult && thumbnailResult.previewUrl) {
      URL.revokeObjectURL(thumbnailResult.previewUrl);
    }

    return {
      imageFileId,
      result: {
        originalUrl,
        thumbnailUrl,
      },
    };
  } catch (error) {
    // 에러 시 썸네일 미리보기 URL 정리
    if (thumbnailResult && thumbnailResult.previewUrl) {
      URL.revokeObjectURL(thumbnailResult.previewUrl);
    }
    console.error('Prepared image upload failed:', error);
    throw error;
  }
};

/**
 * 기존 통합 업로드 함수 (하위 호환성)
 */
export const uploadImageWithMetadata = async (
  bucket: string,
  file: File,
  options: UploadImageOptions,
): Promise<UploadImageResult> => {
  try {
    // 1. 업로드 준비
    const prepResult = await prepareImageForUpload(file);

    // 2. 업로드 실행
    return await createImageFileDocuments(bucket, file, prepResult, options);
  } catch (error) {
    console.error('Image upload workflow failed:', error);
    throw error;
  }
};

/**
 * 썸네일 미리보기 URL을 정리합니다
 */
export const cleanupPreviewUrl = (previewUrl: string): void => {
  try {
    URL.revokeObjectURL(previewUrl);
  } catch (error) {
    console.warn('Failed to cleanup preview URL:', error);
  }
};

/**
 * 여러 이미지를 준비 단계만 실행합니다 (썸네일 생성 및 미리보기용)
 */
export const prepareMultipleImagesForUpload = async (
  files: File[],
): Promise<{ file: File; prepResult: UploadPrepResult | null; error: Error | null }[]> => {
  const results: { file: File; prepResult: UploadPrepResult | null; error: Error | null }[] = [];

  for (const file of files) {
    try {
      const prepResult = await prepareImageForUpload(file);
      results.push({ file, prepResult, error: null });
    } catch (error) {
      console.error(`Failed to prepare ${file.name}:`, error);
      results.push({ file, prepResult: null, error: error as Error });
    }
  }

  return results;
};
